"use client";

import { type TokenAccountInfo, shortenAddress, formatTokenAmount, FORGEPAD_FEE_PERCENT, TYPICAL_RENT_PER_ACCOUNT } from "@/lib/burn";

interface BurnConfirmModalProps {
  token: TokenAccountInfo;
  amount: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function BurnConfirmModal({ token, amount, onConfirm, onCancel }: BurnConfirmModalProps) {
  const tokenName = token.symbol || token.name || shortenAddress(token.mint.toBase58(), 4);

  // Check if burning 100% of balance (account will be closed to reclaim rent)
  const maxAmount = formatTokenAmount(token.amount, token.decimals);
  const isFullBurn = amount === maxAmount;

  // For full burns, we'll attempt to close the account (works for most tokens)
  const willCloseAccount = isFullBurn;

  // Calculate fee breakdown for full burns
  const platformFee = TYPICAL_RENT_PER_ACCOUNT * FORGEPAD_FEE_PERCENT / 100;
  const netRentReclaim = TYPICAL_RENT_PER_ACCOUNT - platformFee;

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-5" onClick={onCancel}>
      <div
        className="bg-[#0a0a0a] rounded-2xl border border-[#333] p-6 max-w-[400px] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2 className="text-xl font-bold text-center mb-4">
          <span className="mr-2">⚠️</span>
          Confirm Burn
        </h2>

        {/* Warning */}
        <div className="bg-[rgba(255,69,0,0.15)] border border-[#FF4500] rounded-lg p-3 text-center text-sm text-[#FF6B35] mb-5">
          This action is PERMANENT and cannot be undone!
        </div>

        {/* Token display */}
        <div className="flex items-center gap-3 bg-[#111] p-4 rounded-lg mb-4">
          {token.logoURI ? (
            <img
              src={token.logoURI}
              alt={tokenName}
              className="w-12 h-12 rounded-full"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#222] flex items-center justify-center text-[#666] font-semibold">
              {(token.symbol?.[0] || "?").toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-semibold text-white">{tokenName}</div>
            <div className="text-xl font-bold text-[#f9cd4f]">
              {amount} {token.symbol || ""}
            </div>
          </div>
        </div>

        {/* Rent reclaim note (only for full burns of legacy SPL tokens) */}
        {willCloseAccount && (
          <div className="bg-[rgba(34,197,94,0.1)] border border-green-600/30 rounded-lg p-3 text-sm text-green-400 mb-4">
            ✓ Token account will be closed — you&apos;ll receive ~{netRentReclaim.toFixed(4)} SOL
          </div>
        )}

        {/* Token-2022 note (full burns only) */}
        {isFullBurn && token.isToken2022 && (
          <div className="bg-[#111] border border-[#333] rounded-lg p-3 text-sm text-[#888] mb-4">
            Token-2022 token — we&apos;ll attempt account closure and rent reclaim after burning. If closure fails for this specific token (some Token-2022 tokens have extensions that prevent it), your tokens will still be burned successfully.
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3.5 bg-[#222] border-none rounded-lg text-[#888] text-base font-semibold cursor-pointer hover:bg-[#2a2a2a] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3.5 bg-gradient-to-br from-[#FF4500] to-[#FF8C00] border-none rounded-lg text-white text-base font-bold cursor-pointer hover:opacity-90 transition-opacity"
          >
            🔥 Burn {amount} {token.symbol || ""}
          </button>
        </div>
      </div>
    </div>
  );
}
